import { z } from "zod";
import { Finite, IsoDate, Symbol_ } from "./primitives";
import { SecurityMeta } from "./market";

export const SleeveId = z.enum([
  "momentum",
  "trendQuality",
  "value",
  "quality",
  "theme",
  "risk",
]);
export type SleeveId = z.infer<typeof SleeveId>;

/**
 * One raw measurement feeding a sleeve.
 *
 * This is the unit of the app's transparency promise: every number shown in
 * the UI traces back to one of these, and every one of these traces back to a
 * provider response. `value: null` means the input was genuinely unavailable,
 * which is displayed rather than hidden.
 */
export const FactorInput = z.object({
  key: z.string(),
  label: z.string(),
  value: Finite.nullable(),
  /** Formatting hint for the UI. */
  unit: z.enum(["ratio", "percent", "currency", "days", "count", "score"]),
  /** Where the number came from, for the "show me" link. */
  source: z.string().nullable(),
});
export type FactorInput = z.infer<typeof FactorInput>;

export const Sleeve = z.object({
  id: SleeveId,
  label: z.string(),
  /**
   * Sector-relative z-score, winsorized to +/-3.
   *
   * Null when too many inputs were missing to compute an honest score. A null
   * sleeve is excluded from the composite and its weight redistributed, rather
   * than being treated as zero — "unknown" and "average" are not the same
   * claim, and conflating them quietly flatters incomplete data.
   */
  zScore: Finite.min(-3).max(3).nullable(),
  weight: Finite.min(0).max(1),
  /** weight * zScore, or null when zScore is null. */
  contribution: Finite.nullable(),
  inputs: z.array(FactorInput),
});
export type Sleeve = z.infer<typeof Sleeve>;

/**
 * A named risk condition. These are surfaced next to every candidate, always
 * visible, never collapsed behind a toggle.
 */
export const RiskFlagId = z.enum([
  "EARNINGS_IMMINENT", // reports within 7 days
  "DEEP_DRAWDOWN", // more than 60% below 1y high
  "HIGH_VOLATILITY", // realized vol above 2x SPY
  "MICROCAP", // market cap below the configured floor
  "LOW_LIQUIDITY", // average dollar volume below the configured floor
  "THIN_HISTORY", // not enough price history to trust the momentum sleeve
  "MISSING_FUNDAMENTALS", // value/quality sleeves are largely blind
  "SINGLE_THEME_DEPENDENT", // score leans heavily on one news theme
]);
export type RiskFlagId = z.infer<typeof RiskFlagId>;

export const RiskFlag = z.object({
  id: RiskFlagId,
  severity: z.enum(["info", "caution", "severe"]),
  /** Human-readable, specific, with the actual number in it. */
  message: z.string(),
});
export type RiskFlag = z.infer<typeof RiskFlag>;

/**
 * A compact price history for the detail chart.
 *
 * Stored as bare closes on a fixed cadence rather than dated points. Repeating
 * an ISO date beside every number roughly quintuples the payload for
 * information the cadence already implies, and this ships for all 215
 * candidates in one file the browser parses on load.
 */
export const PriceHistory = z.object({
  from: IsoDate,
  cadence: z.literal("weekly"),
  closes: z.array(Finite.positive()),
});
export type PriceHistory = z.infer<typeof PriceHistory>;

export const ScoredCandidate = z.object({
  symbol: Symbol_,
  meta: SecurityMeta,
  /**
   * Display score, 0..100, derived from the weighted sleeve sum.
   *
   * This is a *ranking* score, not a prediction, not a price target, and not a
   * recommendation. It answers "where does this sit relative to its sector on
   * the factors we chose", nothing more.
   */
  composite: Finite.min(0).max(100),
  /** Raw weighted sum before mapping to 0..100, kept for backtest attribution. */
  rawScore: Finite,
  rank: z.number().int().positive(),
  sleeves: z.array(Sleeve),
  riskFlags: z.array(RiskFlag),
  /** LLM-written explanation, grounded in the sleeves above. Null if disabled. */
  rationale: z.string().nullable(),
  /** News item IDs referenced by the rationale. */
  newsRefs: z.array(z.string()).default([]),
  lastPrice: Finite.positive().nullable(),
  /** Trailing year of weekly closes. Null when history was too short. */
  priceHistory: PriceHistory.nullable().default(null),
  asOf: IsoDate,
});
export type ScoredCandidate = z.infer<typeof ScoredCandidate>;

/**
 * Sleeve weights, from config/weights.yaml. Surfaced in the UI.
 *
 * The five positive sleeves must sum to 1, which keeps the composite on a
 * stable scale as weights are tuned — otherwise raising one weight quietly
 * rescales every score and makes runs incomparable across config changes.
 *
 * `risk` sits outside that sum because it is subtracted, not blended. It is a
 * penalty multiplier, and giving it a share of the positive budget would let a
 * low-risk name earn points for merely being unremarkable.
 */
export const POSITIVE_SLEEVES = [
  "momentum",
  "trendQuality",
  "value",
  "quality",
  "theme",
] as const satisfies readonly SleeveId[];

export const Weights = z
  .object({
    momentum: Finite.min(0).max(1),
    trendQuality: Finite.min(0).max(1),
    value: Finite.min(0).max(1),
    quality: Finite.min(0).max(1),
    theme: Finite.min(0).max(1),
    /** Applied as a subtraction. Never boosts a score. */
    risk: Finite.min(0).max(1),
  })
  .refine(
    (w) => Math.abs(POSITIVE_SLEEVES.reduce((sum, k) => sum + w[k], 0) - 1) < 1e-6,
    { message: "momentum + trendQuality + value + quality + theme must sum to 1" },
  );
export type Weights = z.infer<typeof Weights>;
